/**
 * 서버 API(Java OffsetDateTime 등)는 UTC 기준 ISO-8601 문자열로 오는 것을 가정합니다.
 * 화면 표시 및 `<input type="datetime-local">` 값은 브라우저 로컬 타임존을 사용합니다.
 */

export function parseApiDateTime(iso: string | undefined | null): Date | null {
  if (iso == null || String(iso).trim() === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * 게시물 목록 작성일: 오늘이면 HH:mm:ss, 그 외 YYYY.MM.DD
 */
export function formatPostListCreatedAt(iso: string | undefined | null): string {
  const d = parseApiDateTime(iso);
  if (!d) return '';
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  return formatDateFromApi(iso);
}

/** 목록/요약용: 로컬 기준 YYYY.MM.DD */
export function formatDateFromApi(iso: string | undefined | null): string {
  const d = parseApiDateTime(iso);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

/** 상세 메타 등: 로컬 날짜+시간 (짧은 스타일) */
export function formatDateTimeFromApi(iso: string | undefined | null): string {
  const d = parseApiDateTime(iso);
  if (!d) return '';
  return d.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
}

/** API(UTC) → `<input type="datetime-local">` value (로컬 wall-clock, 분 단위) */
export function apiDateTimeToDatetimeLocalValue(iso: string | undefined | null): string {
  const d = parseApiDateTime(iso);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** API(UTC) → `<input type="datetime-local" step="1">` 값 (로컬, 시·분·초) */
export function apiDateTimeToDatetimeLocalValueWithSeconds(iso: string | undefined | null): string {
  const d = parseApiDateTime(iso);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * `<input type="datetime-local">` 값은 브라우저에서 로컬 시각으로 해석됩니다.
 * API 전송용 UTC ISO 문자열로 변환합니다.
 */
export function datetimeLocalToIsoUtc(datetimeLocal: string | undefined | null): string {
  if (datetimeLocal == null || String(datetimeLocal).trim() === '') return '';
  const d = new Date(datetimeLocal.trim());
  if (Number.isNaN(d.getTime())) return String(datetimeLocal).trim();
  return d.toISOString();
}